import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  TextField,
} from '@mui/material'
import { Button, useDataProvider, useGetList, useTranslate } from 'react-admin'

import { Stack } from '@mantine/core'

import { LXDatePeriod } from '../../components/LXDatePeriod'
import { BeneficiaryHelper } from '../../helpers/Beneficiary.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Beneficiary } from '../../types/Beneficiary'
import { YoutubeRawImport } from '../../types/YoutubeRawImport'
import genericStyles from './../../generics.module.scss'
import styles from './CreateStatementModal.module.scss'

export interface CreateStatementsSummaryModalProps {
  isOpen?: boolean
  onClose: (created: boolean) => void
}

export const CreateStatementsSummaryModal: React.FC<CreateStatementsSummaryModalProps> = ({
  isOpen = false,
  onClose,
}) => {
  const translate = useTranslate()
  const [creating, setCreating] = React.useState(false)
  const [startMonth, setStartMonth] = useState(
    DateHelper.monthNumber(DateHelper.currentDate().startOf('year')),
  )
  const [startYear, setStartYear] = useState(DateHelper.currentDate().startOf('year').get('y'))

  const [endMonth, setEndMonth] = useState(
    DateHelper.monthNumber(DateHelper.currentDate().subtract(1, 'month')),
  )
  const [endYear, setEndYear] = useState(DateHelper.currentDate().subtract(1, 'month').get('y'))

  const [isAll, setIsAll] = useState(true)
  const [selectedCmsIds, setSelectedCmsIds] = useState<Set<string>>(new Set())
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<Beneficiary[]>([])
  const [error, setError] = useState<string>()
  const dataProvider = useDataProvider()
  const userStore = useUserStore()

  const allBeneficiaries = useGetList<Beneficiary>('beneficiaries', {
    sort: { field: 'id', order: 'ASC' },
    filter: {},
    pagination: { page: 1, perPage: 9999999 },
  }).data

  const cmsList = useMemo(() => userStore.getCMSList(), [userStore])

  const beneficiariesByCms = useMemo(() => {
    if (!allBeneficiaries) return new Map<string, Beneficiary[]>()

    const map = new Map<string, Beneficiary[]>()
    for (const beneficiary of allBeneficiaries) {
      // @ts-ignore - cms field exists in API response but may not be in type
      const cms = beneficiary.cms
      if (cms) {
        const beneficiaries = map.get(cms) ?? []
        beneficiaries.push(beneficiary)
        map.set(cms, beneficiaries)
      }
    }
    return map
  }, [allBeneficiaries])

  const handleCmsCheckboxChange = useCallback((cmsId: string, checked: boolean) => {
    if (checked) {
      setIsAll(false)
      setSelectedCmsIds(prev => new Set(prev).add(cmsId))
    } else {
      setSelectedCmsIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(cmsId)
        return newSet
      })
    }
  }, [])

  const handleAllCheckboxChange = useCallback((checked: boolean) => {
    setIsAll(checked)
    if (checked) {
      setSelectedCmsIds(new Set())
    }
  }, [])

  const isCheckboxModeActive = isAll || selectedCmsIds.size > 0

  useEffect(() => {
    if (!isOpen) {
      setIsAll(true)
      setSelectedCmsIds(new Set())
      setSelectedBeneficiaries([])
      setError(undefined)
      return
    }
    ;(async () => {
      const [last] = await Promise.all([
        dataProvider.getList<YoutubeRawImport>('youtubeRawImports', {
          sort: { field: 'period', order: 'DESC' },
          pagination: { page: 1, perPage: 1 },
          filter: {
            status: '120_corrected',
          },
        }),
      ])
      if (last.data.length > 0) {
        setEndMonth(last.data[0].month)
        setEndYear(last.data[0].year)
      } else {
        setEndMonth(DateHelper.monthNumber(DateHelper.currentDate().subtract(1, 'month')))
        setEndYear(DateHelper.currentDate().subtract(1, 'month').get('y'))
      }
    })()
  }, [isOpen])

  const closeModal = useCallback(
    (isCreated: boolean) => {
      onClose(isCreated)
      setError(undefined)
    },
    [onClose, setError],
  )

  const doDownload = useCallback(async () => {
    setCreating(true)
    setError(undefined)
    try {
      let beneficiariesIds: string[] = []

      if (isAll) {
        beneficiariesIds = []
      } else if (selectedCmsIds.size > 0) {
        beneficiariesIds = Array.from(selectedCmsIds).flatMap(cmsId => {
          return (beneficiariesByCms.get(cmsId) || []).map(b => b.id)
        })
      } else {
        beneficiariesIds = selectedBeneficiaries.map(b => b.id)
      }

      await BeneficiaryHelper.downloadStatementsSummaryCsv({
        start: { month: startMonth, year: startYear },
        end: { month: endMonth, year: endYear },
        beneficiariesIds,
      })
    } catch (e: any) {
      setError(
        e.response?.data?.message ??
          e.response?.data?.content?.fieldError ??
          e.response?.data?.content,
      )
    } finally {
      setCreating(false)
    }
  }, [
    startMonth,
    startYear,
    endMonth,
    endYear,
    isAll,
    selectedCmsIds,
    selectedBeneficiaries,
    beneficiariesByCms,
  ])

  return (
    <Dialog
      aria-labelledby="youtube-import-modal"
      open={isOpen}
      fullWidth
      maxWidth="md"
      onClose={() => closeModal(false)}
    >
      <DialogTitle id="youtube-import-modal">
        {translate('pages.statements.createSummary')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction="row" spacing={6}>
          <Grid container item direction="column" lg={6}>
            <Grid item>
              <InputLabel id="beneficiaries-autocomplete" style={{ fontSize: '80%' }}>
                {translate('pages.beneficiaries._title')}
              </InputLabel>
              <Autocomplete
                id="beneficiaries-autocomplete"
                options={allBeneficiaries ?? []}
                getOptionLabel={option => option.name}
                renderInput={params => <TextField {...params} variant="standard" />}
                value={isCheckboxModeActive ? [] : selectedBeneficiaries}
                onChange={(_, value) => {
                  setSelectedBeneficiaries(value)
                  // Uncheck "all" and CMS checkboxes when manually selecting beneficiaries
                  setIsAll(false)
                  setSelectedCmsIds(new Set())
                }}
                disableCloseOnSelect
                multiple
                disabled={isCheckboxModeActive || creating}
                classes={{
                  tag: genericStyles.tagRemove,
                }}
              />
            </Grid>
            <Grid item>
              <FormGroup>
                <FormControlLabel
                  disabled={creating}
                  control={
                    <Checkbox
                      checked={isAll}
                      size="small"
                      onChange={e => handleAllCheckboxChange(e.target.checked)}
                    />
                  }
                  label={translate('pages.beneficiaries.allBeneficiaries')}
                />
                {cmsList.map(cms => {
                  const isChecked = selectedCmsIds.has(cms.id)
                  const beneficiariesCount = beneficiariesByCms.get(cms.id)?.length || 0
                  return (
                    <FormControlLabel
                      key={cms.id}
                      disabled={creating}
                      control={
                        <Checkbox
                          checked={!isAll && isChecked}
                          size="small"
                          onChange={e => handleCmsCheckboxChange(cms.id, e.target.checked)}
                        />
                      }
                      label={`${cms.name} (${beneficiariesCount})`}
                    />
                  )
                })}
              </FormGroup>
            </Grid>
          </Grid>
          <Grid container item direction="row" lg={6}>
            <Grid item lg={8}>
              <Stack>
                <LXDatePeriod
                  month={startMonth?.toString()}
                  year={startYear?.toString()}
                  monthLabel={translate('_generics.startDate')}
                  yearLabel=" "
                  minYear={DateHelper.january2021Date().get('y')}
                  onUpdate={(month: string | undefined, year: string | undefined): void => {
                    if (month) {
                      setStartMonth(parseInt(month))
                    }
                    if (year) {
                      setStartYear(parseInt(year))
                    }
                  }}
                  disabled={creating}
                />
                <LXDatePeriod
                  month={endMonth?.toString()}
                  year={endYear?.toString()}
                  monthLabel={translate('_generics.endDate')}
                  yearLabel=" "
                  onUpdate={(month: string | undefined, year: string | undefined): void => {
                    if (month) {
                      setEndMonth(parseInt(month))
                    }
                    if (year) {
                      setEndYear(parseInt(year))
                    }
                  }}
                  disabled={creating}
                />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        {creating && (
          <div className={styles.status}>
            <CircularProgress size="1rem" />
          </div>
        )}
        {error && (
          <p className={genericStyles.error}>
            <i>{translate('_generics.errorOccured')}</i>:&nbsp;
            <strong>{error}</strong>
          </p>
        )}
      </DialogContent>
      <DialogActions>
        {!creating && (
          <>
            <Button
              onClick={() => closeModal(false)}
              label={translate('_generics.cancel')}
              disabled={creating && !error}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                doDownload()
              }}
              label={translate('_generics.create')}
              disabled={
                creating ||
                (!isAll && selectedCmsIds.size === 0 && selectedBeneficiaries.length === 0)
              }
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
